import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Calendar as CalendarIcon, CheckCircle2, Circle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Todo, TODO_COLORS, TodoColor, TodoSort, todoColorHex } from "@/components/todo/types";
import { loadTodos, saveTodo, deleteTodo, migrateLocalTodosIfNeeded } from "@/components/todo/storage";
import { toast } from "sonner";

export default function TodoPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [todos, setTodos] = useState<Todo[]>([]);
  const [sort, setSort] = useState<TodoSort>("createdDesc");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Todo> | null>(null);

  // load + sync
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const migrated = await migrateLocalTodosIfNeeded();
        if (migrated > 0) toast.success(`Tarefas migradas para a nuvem (${migrated})`);
      } catch { /* ignore */ }
      try {
        const list = await loadTodos();
        if (!cancelled) setTodos(list);
      } catch {
        toast.error("Erro ao carregar tarefas");
      }
    })();
    const handler = () => {
      loadTodos().then((l) => { if (!cancelled) setTodos(l); }).catch(() => {});
    };
    window.addEventListener("todos:changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("todos:changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // open edit via ?edit=<id> or ?new=<date>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get("edit");
    const newDate = params.get("new");
    if (editId) {
      loadTodos().then((list) => {
        const t = list.find((x) => x.id === editId);
        if (t) {
          setEditing(t);
          setModalOpen(true);
        }
      }).catch(() => {});
      navigate("/todo", { replace: true });
    } else if (newDate) {
      setEditing({ date: newDate, color: "blue" });
      setModalOpen(true);
      navigate("/todo", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSave = async (t: Todo) => {
    try {
      const saved = await saveTodo(t);
      setTodos((prev) => {
        const idx = prev.findIndex((x) => x.id === saved.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = saved;
          return next;
        }
        return [saved, ...prev];
      });
    } catch {
      toast.error("Erro ao salvar tarefa");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTodo(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch {
      toast.error("Erro ao excluir tarefa");
    }
  };

  const toggleComplete = async (id: string) => {
    const t = todos.find((x) => x.id === id);
    if (!t) return;
    const updated = { ...t, completed: !t.completed };
    setTodos((prev) => prev.map((x) => (x.id === id ? updated : x)));
    try {
      await saveTodo(updated);
    } catch {
      toast.error("Erro ao atualizar tarefa");
      setTodos((prev) => prev.map((x) => (x.id === id ? t : x)));
    }
  };

  const sorted = useMemo(() => {
    const arr = [...todos];
    if (sort === "createdDesc") arr.sort((a, b) => b.createdAt - a.createdAt);
    if (sort === "status") arr.sort((a, b) => Number(a.completed) - Number(b.completed));
    if (sort === "dateAsc") {
      arr.sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return a.date.localeCompare(b.date);
      });
    }
    return arr;
  }, [todos, sort]);

  const pending = todos.filter((t) => !t.completed).length;

  return (
    <AppShell>
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1100px] mx-auto">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">To-Do</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {pending} {pending === 1 ? "tarefa pendente" : "tarefas pendentes"} · {todos.length} no total
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as TodoSort)}
              className="h-9 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="createdDesc">Mais recentes</option>
              <option value="dateAsc">Por data</option>
              <option value="status">Por status</option>
            </select>
            <Button
              size="sm"
              onClick={() => {
                setEditing({ color: "blue" });
                setModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nova tarefa
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-sm divide-y divide-border">
          {sorted.length === 0 && (
            <div className="px-6 py-14 text-center">
              <p className="text-sm text-muted-foreground">Nenhuma tarefa ainda. Clique em "Nova tarefa" para começar.</p>
            </div>
          )}
          {sorted.map((t) => (
            <div
              key={t.id}
              className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/40 ${
                t.completed ? "opacity-60" : ""
              }`}
            >
              <button
                onClick={() => toggleComplete(t.id)}
                className="mt-0.5 text-muted-foreground hover:text-primary transition-colors"
                aria-label={t.completed ? "Marcar como pendente" : "Marcar como concluída"}
              >
                {t.completed ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Circle className="h-5 w-5" />
                )}
              </button>
              <span
                className="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: todoColorHex(t.color) }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className={`text-sm font-medium text-foreground truncate ${
                    t.completed ? "line-through" : ""
                  }`}
                >
                  {t.title}
                </div>
                {t.description && (
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</div>
                )}
                {t.date && (
                  <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    {format(parseISO(t.date), "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => {
                    setEditing(t);
                    setModalOpen(true);
                  }}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Editar"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm("Excluir esta tarefa?")) handleDelete(t.id);
                  }}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  aria-label="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <TodoModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </AppShell>
  );
}

interface TodoModalProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: Partial<Todo> | null;
  onSave: (t: Todo) => void;
  onDelete: (id: string) => void;
}

function TodoModal({ open, onOpenChange, initial, onSave, onDelete }: TodoModalProps) {
  const isEdit = !!initial?.id;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [color, setColor] = useState<TodoColor>("blue");
  const [confirmDel, setConfirmDel] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? "");
    setDescription(initial?.description ?? "");
    setDate(initial?.date ?? "");
    setColor((initial?.color as TodoColor) ?? "blue");
    setConfirmDel(false);
  }, [open, initial]);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      title: title.trim(),
      description: description.trim() || undefined,
      date: date || undefined,
      color,
      completed: initial?.completed ?? false,
      createdAt: initial?.createdAt ?? Date.now(),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar tarefa" : "Nova tarefa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="td-title">Título</Label>
            <Input id="td-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Ligar para cliente" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="td-desc">Descrição (opcional)</Label>
            <Textarea id="td-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Data (opcional)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Tarefas com data aparecem no calendário automaticamente.</p>
          </div>
          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {TODO_COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColor(c.id)}
                  title={c.label}
                  className={`h-7 w-7 rounded-full transition-all ${
                    color === c.id ? "ring-2 ring-offset-2 ring-foreground/60 scale-110" : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          {isEdit && initial?.id && (
            confirmDel ? (
              <div className="flex w-full sm:w-auto gap-2 mr-auto">
                <Button variant="destructive" size="sm" onClick={() => { onDelete(initial.id!); onOpenChange(false); }}>
                  Confirmar exclusão
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)}>Cancelar</Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="mr-auto text-destructive hover:text-destructive" onClick={() => setConfirmDel(true)}>
                <Trash2 className="h-4 w-4" /> Excluir
              </Button>
            )
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={!title.trim()}>{isEdit ? "Salvar" : "Criar tarefa"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}